import React from "react";
import {
  Alert,
  Box,
  Button,
  Container,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import logo from "../../images/LogoNavbar1.svg";

const BRAND = { orange: "#F35A3A", dark: "#19242F" };

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = React.useState({ name: "", email: "", password: "" });
  const [error, setError] = React.useState("");
  const [loading, setLoading] = React.useState(false);

  const handleChange = (event) => {
    setForm((prev) => ({ ...prev, [event.target.name]: event.target.value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      setLoading(true);
      setError("");
      const data = await register(form);

      if (data?.onboarding?.firstUser) {
        navigate("/dashboard", { replace: true });
        return;
      }

      if (data?.onboarding?.requiresEmailVerification) {
        navigate(`/confirmar-email?email=${encodeURIComponent(form.email.trim().toLowerCase())}`, {
          replace: true,
          state: {
            email: form.email.trim().toLowerCase(),
            message: data?.onboarding?.message || "Cuenta creada. Revisá tu email para confirmar el correo.",
          },
        });
        return;
      }

      navigate("/login", {
        replace: true,
        state: { message: data?.onboarding?.message || "Cuenta creada. Esperá la aprobación de WebProfit." },
      });
    } catch (err) {
      setError(err.message || "No se pudo crear la cuenta");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#F6F7FB", display: "grid", placeItems: "center", px: 2 }}>
      <Container maxWidth="xs">
        <Paper elevation={0} sx={{ p: { xs: 3, sm: 4 }, borderRadius: 4, border: "1px solid rgba(25,36,47,0.08)" }}>
          <Stack alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
            <Box component="img" src={logo} alt="WebProfit" sx={{ width: 64, height: 64, objectFit: "contain" }} />
            <Box sx={{ textAlign: "center" }}>
              <Typography variant="h4" sx={{ fontWeight: 950, color: BRAND.dark }}>
                Crear usuario
              </Typography>
              <Typography sx={{ color: "rgba(25,36,47,0.65)", mt: 0.5 }}>
                El primer usuario queda como superadmin. Los demás deben confirmar email y esperar aprobación.
              </Typography>
            </Box>
          </Stack>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <Box component="form" onSubmit={handleSubmit}>
            <Stack spacing={2}>
              <TextField label="Nombre" name="name" value={form.name} onChange={handleChange} required fullWidth />
              <TextField label="Email" name="email" type="email" value={form.email} onChange={handleChange} required fullWidth />
              <TextField label="Contraseña" name="password" type="password" value={form.password} onChange={handleChange} required fullWidth inputProps={{ minLength: 6 }} />
              <Button type="submit" disabled={loading} variant="contained" size="large" sx={{ bgcolor: BRAND.orange, borderRadius: 999, textTransform: "none", fontWeight: 900, py: 1.25 }}>
                {loading ? "Creando..." : "Crear cuenta"}
              </Button>
            </Stack>
          </Box>

          <Typography sx={{ textAlign: "center", mt: 3, color: "rgba(25,36,47,0.7)" }}>
            ¿Ya tenés usuario?{" "}
            <Link component={RouterLink} to="/login" sx={{ color: BRAND.orange, fontWeight: 900 }}>
              Ingresar
            </Link>
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
}
